import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import ArrowBack from '@mui/icons-material/ArrowBack';
import AddPhotoAlternate from '@mui/icons-material/AddPhotoAlternate';
import Edit from '@mui/icons-material/Edit';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import PageHeader from '../../../Components/PageHeader';
import { useApiGet, useApiSend } from '../../../utils/httpClientTs';
import {
  getOnlineItem,
  uploadOnlineItemImage,
  addOnlineItemImage,
  deleteOnlineItemImage,
  updateOnlineItem,
  getCategories,
  getGroupTypes,
} from '../../../endpoints/chapterSupplies';
import { getDocumentList } from '../../../endpoints/documents';
import type { UpdateOnlineItemRequest } from '../../../types/chapterSupplies';
import type { DocumentList } from '../../../types/document';

const DOCUMENT_SEARCH_DEBOUNCE_MS = 300;

function selectedNames(
  ids: string[],
  options: { id: string; name: string }[],
  maxShow = 3
): string {
  if (!ids.length) return 'None';
  const names = ids
    .map((id) => options.find((o) => o.id === id)?.name)
    .filter(Boolean) as string[];
  if (names.length > maxShow) {
    return `${names.slice(0, maxShow).join(', ')} +${names.length - maxShow} more`;
  }
  return names.join(', ');
}

export default function OnlineItemDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [documentSearchInput, setDocumentSearchInput] = useState('');
  const [documentSearch, setDocumentSearch] = useState('');

  useEffect(() => {
    const t = setTimeout(
      () => setDocumentSearch(documentSearchInput),
      DOCUMENT_SEARCH_DEBOUNCE_MS
    );
    return () => clearTimeout(t);
  }, [documentSearchInput]);

  const {
    data: detail,
    isFetching,
    refetch,
  } = useApiGet(
    ['chapter-supplies-online-item', id],
    () => getOnlineItem(id!),
    { enabled: !!id, retry: 1 }
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [newImageSortOrder, setNewImageSortOrder] = useState(0);
  const [editForm, setEditForm] = useState<UpdateOnlineItemRequest>({
    category_ids: [],
    sub_category_ids: [],
    group_type_ids: [],
  });

  const { data: categories = [] } = useApiGet(
    ['chapter-supplies-categories'],
    getCategories,
    { enabled: !!id, retry: 1 }
  );
  const { data: groupTypes = [] } = useApiGet(
    ['chapter-supplies-group-types'],
    getGroupTypes,
    { enabled: !!id, retry: 1 }
  );
  const allSubcategories = useMemo(
    () => categories.flatMap((c) => c.subcategories ?? []),
    [categories]
  );
  const { data: documentsDataSearch } = useApiGet(
    ['library-documents-search', documentSearch],
    () =>
      getDocumentList({
        search: documentSearch || undefined,
        per_page: 50,
        order: 'asc',
        sort_by: 'name',
      }),
    { enabled: !!id && editing, retry: 1 }
  );
  const { data: documentsDataView } = useApiGet(
    ['library-documents-for-selector'],
    () => getDocumentList({ per_page: 100, order: 'asc', sort_by: 'name' }),
    { enabled: !!id && !editing && !!detail?.library_document_id, retry: 1 }
  );
  const libraryDocuments: DocumentList[] = editing
    ? (documentsDataSearch?.data ?? [])
    : (documentsDataView?.data ?? []);

  const { mutate: doUploadAndAdd } = useApiSend(
    async (file: File) => {
      const { url } = await uploadOnlineItemImage(file);
      if (!url?.trim()) {
        throw new Error('Upload did not return a URL');
      }
      await addOnlineItemImage(id!, {
        image_url: url,
        sort_order: newImageSortOrder,
      });
    },
    () => {
      setUploading(false);
      setUploadError(null);
      refetch();
    },
    (err) => {
      setUploading(false);
      setUploadError(err?.message ?? 'Upload failed');
    },
    [['chapter-supplies-online-item', id]]
  );

  const { mutate: doUpdate, isPending: isUpdating } = useApiSend(
    (payload: UpdateOnlineItemRequest) => updateOnlineItem(id!, payload),
    () => {
      setEditError(null);
      setEditing(false);
      refetch();
    },
    (err) => {
      setEditError(err?.message ?? 'Update failed');
    },
    [['chapter-supplies-online-item', id]]
  );

  const { mutate: doRemoveImage, isPending: isRemovingImage } = useApiSend(
    (imageId: string) => deleteOnlineItemImage(id!, imageId),
    () => {
      setUploadError(null);
      refetch();
    },
    (err) => setUploadError(err?.message ?? 'Failed to remove image'),
    [['chapter-supplies-online-item', id]]
  );

  const handleAddImageClick = () => fileInputRef.current?.click();
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !id) return;
    e.target.value = '';
    setUploadError(null);
    setUploading(true);
    doUploadAndAdd(file);
  };

  useEffect(() => {
    if (detail?.images) {
      setNewImageSortOrder(detail.images.length);
    }
  }, [detail?.images]);

  if (!id) {
    return (
      <Grid container paddingLeft={2} paddingRight={2}>
        <Typography color="error">Missing online item ID.</Typography>
      </Grid>
    );
  }

  if (isFetching && !detail) {
    return (
      <Grid container paddingLeft={2} paddingRight={2}>
        <Typography>Loading…</Typography>
      </Grid>
    );
  }

  if (!detail) {
    return (
      <Grid container paddingLeft={2} paddingRight={2}>
        <Typography color="error">Online item not found.</Typography>
      </Grid>
    );
  }

  return (
    <Grid container paddingLeft={2} paddingRight={2}>
      <Grid item xs={12}>
        <PageHeader
          title={detail.item_name ?? 'Online item'}
          leftDom={
            <Button
              startIcon={<ArrowBack />}
              onClick={() =>
                navigate('/chapter-supplies-management/online-items')
              }
            >
              Back to list
            </Button>
          }
          rightDom={
            editing ? (
              <Button
                variant="outlined"
                onClick={() => {
                  setEditing(false);
                  setEditError(null);
                }}
              >
                Cancel
              </Button>
            ) : (
              <Button
                variant="contained"
                startIcon={<Edit />}
                onClick={() => {
                  setEditing(true);
                  setEditError(null);
                  setEditForm({
                    price: detail.price,
                    is_featured: detail.is_featured,
                    is_active: detail.is_active,
                    short_description: detail.short_description ?? null,
                    sort_order: detail.sort_order,
                    library_document_id: detail.library_document_id ?? null,
                    max_quantity_per_order:
                      detail.max_quantity_per_order ?? null,
                    category_ids: detail.category_ids ?? [],
                    sub_category_ids: detail.sub_category_ids ?? [],
                    group_type_ids: detail.group_type_ids ?? [],
                  });
                }}
              >
                Edit
              </Button>
            )
          }
          subtitle={undefined}
        />
      </Grid>
      <Grid item xs={12} sx={{ mt: 2 }}>
        <Paper sx={{ p: 2 }}>
          {editing ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                doUpdate(editForm);
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Item (read-only)
                  </Typography>
                  <Typography variant="body1">
                    {detail.item_name ?? '—'} ({detail.item_id})
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    inputProps={{ min: 0, step: 0.01 }}
                    label="Price"
                    value={editForm.price ?? ''}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        price:
                          e.target.value === ''
                            ? undefined
                            : Number(e.target.value),
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={(editForm.is_featured ?? 0) === 1}
                        onChange={(e) =>
                          setEditForm((prev) => ({
                            ...prev,
                            is_featured: e.target.checked ? 1 : 0,
                          }))
                        }
                      />
                    }
                    label="Featured"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={(editForm.is_active ?? 1) === 1}
                        onChange={(e) =>
                          setEditForm((prev) => ({
                            ...prev,
                            is_active: e.target.checked ? 1 : 0,
                          }))
                        }
                      />
                    }
                    label="Active"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Short description"
                    value={editForm.short_description ?? ''}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        short_description: e.target.value || null,
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    inputProps={{ min: 0, step: 1 }}
                    label="Sort order"
                    value={editForm.sort_order ?? ''}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        sort_order:
                          e.target.value === ''
                            ? undefined
                            : Number(e.target.value),
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Autocomplete<DocumentList>
                    fullWidth
                    options={libraryDocuments}
                    getOptionLabel={(opt) => opt.name}
                    value={
                      editForm.library_document_id
                        ? (libraryDocuments.find(
                            (d) => d.id === editForm.library_document_id
                          ) ??
                          ({
                            id: editForm.library_document_id,
                            name: editForm.library_document_id,
                          } as DocumentList))
                        : null
                    }
                    onChange={(_, newValue) =>
                      setEditForm((prev) => ({
                        ...prev,
                        library_document_id: newValue?.id ?? null,
                      }))
                    }
                    onInputChange={(_, value) => setDocumentSearchInput(value)}
                    filterOptions={(x) => x}
                    isOptionEqualToValue={(opt, val) => opt.id === val.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Library document"
                        placeholder="Search documents..."
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    inputProps={{ min: 0, step: 1 }}
                    label="Max quantity per order"
                    value={editForm.max_quantity_per_order ?? ''}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        max_quantity_per_order:
                          e.target.value === '' ? null : Number(e.target.value),
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel id="edit-categories-label">
                      Categories
                    </InputLabel>
                    <Select
                      labelId="edit-categories-label"
                      label="Categories"
                      multiple
                      value={editForm.category_ids ?? []}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          category_ids: e.target.value as string[],
                        }))
                      }
                      renderValue={(selected) =>
                        selectedNames(selected as string[], categories)
                      }
                    >
                      {categories.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel id="edit-subcategories-label">
                      Subcategories
                    </InputLabel>
                    <Select
                      labelId="edit-subcategories-label"
                      label="Subcategories"
                      multiple
                      value={editForm.sub_category_ids ?? []}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          sub_category_ids: e.target.value as string[],
                        }))
                      }
                      renderValue={(selected) =>
                        selectedNames(selected as string[], allSubcategories)
                      }
                    >
                      {allSubcategories.map((s) => (
                        <MenuItem key={s.id} value={s.id}>
                          {s.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel id="edit-group-types-label">
                      Group types
                    </InputLabel>
                    <Select
                      labelId="edit-group-types-label"
                      label="Group types"
                      multiple
                      value={editForm.group_type_ids ?? []}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          group_type_ids: e.target.value as string[],
                        }))
                      }
                      renderValue={(selected) =>
                        selectedNames(selected as string[], groupTypes)
                      }
                    >
                      {groupTypes.map((g) => (
                        <MenuItem key={g.id} value={g.id}>
                          {g.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {editError && (
                  <Grid item xs={12}>
                    <Typography color="error" variant="body2">
                      {editError}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isUpdating}
                  >
                    {isUpdating ? 'Saving…' : 'Save changes'}
                  </Button>
                </Grid>
              </Grid>
            </form>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Item
                </Typography>
                <Typography variant="body1">
                  {detail.item_name ?? '—'} ({detail.item_id})
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Price
                </Typography>
                <Typography variant="body1">
                  {typeof detail.price === 'number'
                    ? `$${detail.price.toFixed(2)}`
                    : '—'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Featured
                </Typography>
                <Typography variant="body1">
                  {detail.is_featured === 1 ? 'Yes' : 'No'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Active
                </Typography>
                <Typography variant="body1">
                  {detail.is_active === 1 ? 'Yes' : 'No'}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Short description
                </Typography>
                <Typography variant="body1">
                  {detail.short_description ?? '—'}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Images
                </Typography>
                <Box
                  sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}
                >
                  {(detail.images ?? []).map((img) => (
                    <Box
                      key={img.id}
                      sx={{
                        position: 'relative',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                      }}
                    >
                      <Box
                        component="img"
                        src={img.image_url}
                        alt=""
                        sx={{
                          width: 80,
                          height: 80,
                          objectFit: 'cover',
                        }}
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ px: 0.5, py: 0.25 }}
                      >
                        Order: {img.sort_order}
                      </Typography>
                      <Button
                        size="small"
                        color="error"
                        startIcon={<DeleteOutline />}
                        onClick={() => doRemoveImage(img.id)}
                        disabled={isRemovingImage}
                        sx={{ minWidth: 0, py: 0.25 }}
                      >
                        Remove
                      </Button>
                    </Box>
                  ))}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                      size="small"
                      type="number"
                      inputProps={{ min: 0, step: 1 }}
                      label="Sort order for new image"
                      sx={{ width: 160 }}
                      value={newImageSortOrder}
                      onChange={(e) =>
                        setNewImageSortOrder(
                          e.target.value === '' ? 0 : Number(e.target.value)
                        )
                      }
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddPhotoAlternate />}
                      onClick={handleAddImageClick}
                      disabled={uploading}
                    >
                      {uploading ? 'Uploading…' : 'Add image'}
                    </Button>
                  </Box>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                  />
                </Box>
                {uploadError && (
                  <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                    {uploadError}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Sort order
                </Typography>
                <Typography variant="body1">
                  {detail.sort_order ?? '—'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Library document
                </Typography>
                <Typography variant="body1">
                  {detail.library_document_id
                    ? (libraryDocuments.find(
                        (d) => d.id === detail.library_document_id
                      )?.name ?? detail.library_document_id)
                    : '—'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Max quantity per order
                </Typography>
                <Typography variant="body1">
                  {detail.max_quantity_per_order != null
                    ? String(detail.max_quantity_per_order)
                    : '—'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Categories
                </Typography>
                <Typography variant="body1">
                  {detail.category_ids?.length
                    ? selectedNames(
                        detail.category_ids,
                        categories,
                        Number.POSITIVE_INFINITY
                      )
                    : '—'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Subcategories
                </Typography>
                <Typography variant="body1">
                  {detail.sub_category_ids?.length
                    ? selectedNames(
                        detail.sub_category_ids,
                        allSubcategories,
                        Number.POSITIVE_INFINITY
                      )
                    : '—'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Group types
                </Typography>
                <Typography variant="body1">
                  {detail.group_type_ids?.length
                    ? selectedNames(
                        detail.group_type_ids,
                        groupTypes,
                        Number.POSITIVE_INFINITY
                      )
                    : '—'}
                </Typography>
              </Grid>
            </Grid>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
}
